import React from 'react';

export default function ValueSection() {
  const capabilities = [
    {
      title: 'Student & Teacher Management',
      description: 'Centralize user accounts, role permissions, subject assignments, and profile records across all departments.',
      icon: '👥',
      tag: 'Administration',
      gradient: 'from-blue-500/20 to-indigo-500/10',
      border: 'border-blue-500/30',
    },
    {
      title: 'Attendance Tracking',
      description: 'Log daily attendance in seconds. Monitor individual and class attendance trends with automatic alerts.',
      icon: '✅',
      tag: 'Daily Operations',
      gradient: 'from-emerald-500/20 to-teal-500/10',
      border: 'border-emerald-500/30',
    },
    {
      title: 'Quizzes & Assessments',
      description: 'Create timed multiple-choice quizzes with instant auto-grading and detailed student score analysis.',
      icon: '⚡',
      tag: 'Academics',
      gradient: 'from-purple-500/20 to-pink-500/10',
      border: 'border-purple-500/30',
    },
    {
      title: 'Assignments & Submissions',
      description: 'Assign coursework, receive digital student submissions, provide teacher feedback, and record grades.',
      icon: '📝',
      tag: 'Classroom',
      gradient: 'from-amber-500/20 to-orange-500/10',
      border: 'border-amber-500/30',
    },
    {
      title: 'Timetable Management',
      description: 'Organize weekly subject schedules and room assignments for administrators, teachers, and students.',
      icon: '📅',
      tag: 'Scheduling',
      gradient: 'from-indigo-500/20 to-cyan-500/10',
      border: 'border-indigo-500/30',
    },
    {
      title: 'Academic Performance & Grade Book',
      description: 'Centralize term grades, attendance percentages, and weighted scores into a unified grade book.',
      icon: '📊',
      tag: 'Analytics',
      gradient: 'from-teal-500/20 to-emerald-500/10',
      border: 'border-teal-500/30',
    },
    {
      title: 'Parent Visibility',
      description: 'Empower parents with real-time access to their child’s attendance, quiz performance, and teacher notes.',
      icon: '👨‍👩‍👦',
      tag: 'Parent Portal',
      gradient: 'from-rose-500/20 to-pink-500/10',
      border: 'border-rose-500/30',
    },
    {
      title: 'Messaging & Notifications',
      description: 'Keep administrators, teachers, parents, and students connected with targeted announcements and direct chat.',
      icon: '💬',
      tag: 'Communication',
      gradient: 'from-sky-500/20 to-blue-500/10',
      border: 'border-sky-500/30',
    },
    {
      title: 'PDF Report Generation',
      description: 'Instantly generate and download official academic report cards and attendance summaries in PDF format.',
      icon: '📄',
      tag: 'Reporting',
      gradient: 'from-violet-500/20 to-purple-500/10',
      border: 'border-violet-500/30',
    },
  ];

  return (
    <section id="solutions" className="py-24 bg-slate-950 relative overflow-hidden">
      {/* Background accents */}
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-3">
            COMPLETE EDTECH PLATFORM
          </div>
          <h2 className="font-serif text-3xl sm:text-5xl font-extrabold text-white tracking-tight mb-4">
            Everything Your School Needs, In One Place
          </h2>
          <p className="text-slate-400 text-base sm:text-lg">
            EduPortal replaces fragmented tools with a single integrated workspace for school operational excellence.
          </p>
        </div>

        {/* Capabilities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {capabilities.map((cap, idx) => (
            <div
              key={idx}
              className={`group relative bg-slate-900/60 border ${cap.border} rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-950/40 hover:bg-slate-900/90 flex flex-col justify-between overflow-hidden`}
            >
              <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${cap.gradient} rounded-full blur-2xl pointer-events-none group-hover:scale-150 transition-transform duration-500`} />

              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-3xl p-3 bg-white/5 border border-white/10 rounded-xl group-hover:scale-110 transition-transform duration-300">
                    {cap.icon}
                  </span>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 bg-white/5 border border-white/10 px-3 py-1 rounded-full">
                    {cap.tag}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-300 transition-colors">
                  {cap.title}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {cap.description}
                </p>
              </div>

              {/* Card Footer Micro Preview Link */}
              <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between text-xs font-semibold text-blue-400 group-hover:text-blue-300">
                <span>View capability</span>
                <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
